/**
 * XLE Custom Bulk Order Form Handler - V2
 * Team member-centric flow with file uploads
 */

class CustomBulkOrderFormV2 {
  constructor() {
    this.form = document.getElementById('custom-bulk-order-form');
    if (!this.form) return;

    this.teamMembersContainer = document.getElementById('team-members-container');
    this.memberTemplate = document.getElementById('team-member-template');
    this.itemTemplate = document.getElementById('member-item-template');
    this.addMemberBtn = document.getElementById('add-team-member');
    this.submitBtn = document.getElementById('submit-order');
    this.messagesDiv = document.getElementById('form-messages');
    this.totalMembersCount = document.getElementById('total-members-count');
    this.totalItemsCount = document.getElementById('total-items-count');

    this.memberCounter = 0;
    this.uploadedFiles = {
      design: [],
      logo: []
    };

    // Load products data
    this.loadProductsData();

    this.init();
  }

  loadProductsData() {
    const productsScript = document.getElementById('products-data');
    if (productsScript) {
      try {
        this.productsData = JSON.parse(productsScript.textContent);
        this.sizeCharts = this.productsData.sizeCharts || [];
      } catch (e) {
        console.error('Failed to parse products data:', e);
        this.productsData = { products: [] };
        this.sizeCharts = [];
      }
    } else {
      console.error('Products data script not found');
      this.productsData = { products: [] };
      this.sizeCharts = [];
    }
  }

  init() {
    // Add initial team member
    this.addTeamMember();

    // Event listeners
    this.addMemberBtn.addEventListener('click', () => this.addTeamMember());
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // File upload handlers
    document.getElementById('design-files').addEventListener('change', (e) =>
      this.handleFileUpload(e, 'design', 'design-files-preview')
    );
    document.getElementById('logo-files').addEventListener('change', (e) =>
      this.handleFileUpload(e, 'logo', 'logo-files-preview')
    );

    // Listen for quantity changes
    this.teamMembersContainer.addEventListener('input', (e) => {
      if (e.target.classList.contains('item-quantity')) {
        this.updateTotals();
      }
    });

    // Listen for name changes
    this.teamMembersContainer.addEventListener('input', (e) => {
      if (e.target.classList.contains('member-name-input')) {
        this.updateMemberNameDisplay(e.target);
      }
    });

    // Contact info Edit/Save/Cancel buttons
    this.originalContactData = {};
    document.getElementById('edit-contact-btn').addEventListener('click', () => this.enableContactEditing());
    document.getElementById('save-contact-btn').addEventListener('click', () => this.saveContactInfo());
    document.getElementById('cancel-contact-btn').addEventListener('click', () => this.cancelContactEditing());
  }

  handleFileUpload(event, type, previewId) {
    const files = Array.from(event.target.files);
    const previewContainer = document.getElementById(previewId);

    files.forEach(file => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.showMessage(`File "${file.name}" is too large. Maximum size is 10MB.`, 'error');
        return;
      }

      // Store file reference
      this.uploadedFiles[type].push(file);

      // Add preview
      this.addFilePreview(file.name, previewContainer, type);
    });
  }

  addFilePreview(fileName, container, type) {
    const preview = document.createElement('div');
    preview.className = 'file-preview-item';
    preview.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"/>
      </svg>
      <span>${fileName}</span>
      <button type="button" class="remove-file-btn" data-filename="${fileName}" data-type="${type}">
        <svg width="14" height="14" viewBox="0 0 20 20">
          <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    const removeBtn = preview.querySelector('.remove-file-btn');
    removeBtn.addEventListener('click', () => {
      this.removeFile(fileName, type);
      preview.remove();
    });

    container.appendChild(preview);
  }

  removeFile(fileName, type) {
    this.uploadedFiles[type] = this.uploadedFiles[type].filter(f => f.name !== fileName);
  }

  addTeamMember() {
    this.memberCounter++;

    const template = this.memberTemplate.content.cloneNode(true);
    const memberCard = template.querySelector('.team-member-card');

    // Set member index and number
    memberCard.setAttribute('data-member-index', this.memberCounter);
    memberCard.querySelector('.member-number-text').textContent = this.memberCounter;

    // Add member name input handler
    const nameInput = memberCard.querySelector('.member-name-input');
    nameInput.addEventListener('input', (e) => this.updateMemberNameDisplay(e.target));

    // Add remove button handler
    const removeBtn = memberCard.querySelector('.remove-member-btn');
    removeBtn.addEventListener('click', () => this.removeTeamMember(memberCard));

    // Add "Add Item" button handler
    const addItemBtn = memberCard.querySelector('.add-item-to-member');
    addItemBtn.addEventListener('click', () => this.addItemToMember(memberCard));

    // Add initial item
    this.teamMembersContainer.appendChild(template);
    this.addItemToMember(memberCard);

    this.updateTotals();
  }

  removeTeamMember(memberCard) {
    const members = this.teamMembersContainer.querySelectorAll('.team-member-card');
    if (members.length <= 1) {
      this.showMessage('You must have at least one team member.', 'error');
      return;
    }

    memberCard.remove();
    this.updateMemberNumbers();
    this.updateTotals();
  }

  updateMemberNumbers() {
    const members = this.teamMembersContainer.querySelectorAll('.team-member-card');
    members.forEach((member, index) => {
      member.querySelector('.member-number-text').textContent = index + 1;
    });
  }

  updateMemberNameDisplay(input) {
    const memberCard = input.closest('.team-member-card');
    const nameDisplay = memberCard.querySelector('.member-name-display');
    nameDisplay.textContent = input.value || 'this member';
  }

  addItemToMember(memberCard) {
    const itemsContainer = memberCard.querySelector('.member-items-container');
    const template = this.itemTemplate.content.cloneNode(true);
    const item = template.querySelector('.member-item');

    // Add event handlers
    const productSelect = item.querySelector('.item-product');
    const genderSelect = item.querySelector('.item-gender');
    const sizeSelect = item.querySelector('.item-size');
    const fitGroup = item.querySelector('.item-fit-group');

    // Product change handler
    productSelect.addEventListener('change', () => {
      const selectedOption = productSelect.options[productSelect.selectedIndex];
      const productType = selectedOption.dataset.type || '';
      const productTitle = selectedOption.dataset.title || '';
      const productId = productSelect.value;

      // Show/hide fit type for jerseys only - check both type and title
      if (productType.toLowerCase().includes('jersey') || productTitle.toLowerCase().includes('jersey')) {
        fitGroup.style.display = 'block';
      } else {
        fitGroup.style.display = 'none';
      }

      // Check if product has size-related options
      const product = this.productsData.products.find(p => p.id == productId);

      if (product && product.options) {
        // Check if product has any size or gender options
        const hasSizeOption = product.options.some(opt =>
          opt.name && (
            opt.name.toLowerCase().includes('size') ||
            opt.name.toLowerCase() === 'gender'
          )
        );

        if (!hasSizeOption) {
          // Product has no size/gender options (like hats)
          console.log('Product has no size options - auto-selecting first variant');

          // Auto-select the first variant and hide all size/gender fields
          if (product.variants && product.variants.length > 0) {
            sizeSelect.innerHTML = `<option value="${product.variants[0].id}" selected>One Size</option>`;
          }

          sizeSelect.disabled = true;
          genderSelect.disabled = true;
          genderSelect.value = 'Unisex';

          // Hide both gender and size fields completely
          genderSelect.closest('.form-group-inline').style.display = 'none';
          sizeSelect.closest('.form-group-inline').style.display = 'none';

          // Update price immediately since variant is auto-selected
          this.updateTotals();
        } else {
          // Product has size variants - show gender/size fields normally
          genderSelect.disabled = false;
          genderSelect.closest('.form-group-inline').style.display = 'flex';
          sizeSelect.closest('.form-group-inline').style.display = 'flex';
          genderSelect.value = '';
          sizeSelect.value = '';
          sizeSelect.innerHTML = '<option value="">Select gender first</option>';
          sizeSelect.disabled = true;

          const sizeLabel = sizeSelect.closest('.form-group-inline').querySelector('label');
          if (sizeLabel) sizeLabel.textContent = 'Size *';
        }
      }
    });

    // Gender change handler
    genderSelect.addEventListener('change', () => {
      const productId = productSelect.value;
      if (productId) {
        this.populateSizes(genderSelect.value, productId, sizeSelect);
      }
    });

    // Size change handler - update totals when size is selected
    sizeSelect.addEventListener('change', () => {
      this.updateTotals();
    });

    // Add remove button handler
    const removeBtn = item.querySelector('.remove-item-btn');
    removeBtn.addEventListener('click', () => {
      const items = itemsContainer.querySelectorAll('.member-item');
      if (items.length <= 1) {
        this.showMessage('Each member must have at least one item.', 'error');
        return;
      }
      item.remove();
      this.updateTotals();
    });

    itemsContainer.appendChild(template);
    this.updateTotals();
  }

  populateSizes(gender, productId, sizeSelect) {
    // Clear and reset
    sizeSelect.innerHTML = '<option value="">Select size</option>';
    sizeSelect.disabled = false;

    if (!gender || !productId) {
      sizeSelect.disabled = true;
      return;
    }

    // Find the product
    const product = this.productsData.products.find(p => p.id == productId);
    if (!product || !product.variants || !product.options) {
      sizeSelect.disabled = true;
      sizeSelect.innerHTML = '<option value="">No sizes available</option>';
      return;
    }

    // Look for the gender size option (e.g., "Men's Size" or "Women's Size")
    const genderOptionName = `${gender}'s Size`;
    let sizeOption = product.options.find(opt => opt.name === genderOptionName);

    // Fallback: Try just "Size" option if gender-specific not found
    if (!sizeOption) {
      sizeOption = product.options.find(opt => opt.name && opt.name.toLowerCase() === 'size');

      if (!sizeOption) {
        console.log(`No size option found for "${genderOptionName}" or "Size". Available options:`, product.options.map(o => o.name));
        sizeSelect.innerHTML = '<option value="">Product not configured with sizes yet</option>';
        sizeSelect.disabled = true;
        return;
      }

      // If using generic "Size" option, we'll filter by gender later if Gender option exists
      console.log(`Using generic "Size" option as fallback for ${gender}'s sizes`);
    }

    // Get the position of the size option (1, 2, or 3)
    const optionPosition = sizeOption.position;
    const optionKey = `option${optionPosition}`;

    // Check if there's a Category/Gender option to filter by
    const categoryOption = product.options.find(opt =>
      opt.name && (
        opt.name.toLowerCase() === 'gender' ||
        opt.name.toLowerCase() === 'category'
      )
    );
    let filteredVariants = product.variants;

    if (categoryOption) {
      // Filter variants by the selected gender/category
      const categoryPosition = categoryOption.position;
      const categoryKey = `option${categoryPosition}`;

      // Normalize gender search (handle "Men" vs "Mens", "Women" vs "Womens")
      const searchGender = gender.toLowerCase().replace("'s", "");

      filteredVariants = product.variants.filter(variant => {
        const variantCategory = variant[categoryKey];
        if (!variantCategory) return false;

        const normalizedCategory = variantCategory.toLowerCase().replace("'s", "");
        // Check if either contains the other (handles "Men"/"Mens", "Women"/"Womens")
        return normalizedCategory.includes(searchGender) || searchGender.includes(normalizedCategory);
      });

      console.log(`Filtered ${filteredVariants.length} variants for ${gender} using ${categoryOption.name} option`);
    }

    // Group variants by size (in case there are color/other options)
    const sizeMap = new Map();

    filteredVariants.forEach(variant => {
      const sizeValue = variant[optionKey];
      if (sizeValue && !sizeMap.has(sizeValue)) {
        // Store first variant with this size
        sizeMap.set(sizeValue, variant.id);
      }
    });

    // Sort sizes in standard order
    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', 'XXXL'];
    const sortedSizes = Array.from(sizeMap.keys()).sort((a, b) => {
      const indexA = sizeOrder.indexOf(a);
      const indexB = sizeOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    // Populate size dropdown
    sortedSizes.forEach(size => {
      const option = document.createElement('option');
      option.value = sizeMap.get(size); // Use the variant ID
      option.textContent = size;
      option.dataset.sizeValue = size;
      sizeSelect.appendChild(option);
    });

    // If no sizes found
    if (sizeSelect.options.length === 1) {
      sizeSelect.innerHTML = '<option value="">No sizes available</option>';
      sizeSelect.disabled = true;
    }
  }

  populateVariants(productId, variantSelect) {
    // Find product in products data
    const product = this.productsData.products.find(p => p.id == productId);

    if (!product) {
      variantSelect.disabled = true;
      variantSelect.innerHTML = '<option value="">Product not found</option>';
      return;
    }

    // Update the label and help text based on product options
    const formGroup = variantSelect.closest('.form-group-inline');
    const variantLabel = formGroup.querySelector('label');
    const helpText = formGroup.querySelector('.variant-help-text');
    const sizeOption = product.options.find(opt => opt.name && opt.name.toLowerCase() === 'size');
    const colorOption = product.options.find(opt => opt.name && (opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'));

    if (sizeOption && colorOption) {
      variantLabel.textContent = 'Size / Color *';
      helpText.textContent = 'Select size and color';
    } else if (sizeOption) {
      variantLabel.textContent = 'Size *';
      helpText.textContent = 'Select your size';
    } else if (colorOption) {
      variantLabel.textContent = 'Color *';
      helpText.textContent = 'Select color';
    } else {
      variantLabel.textContent = 'Option *';
      helpText.textContent = 'Select option';
    }

    // Clear and populate variants
    variantSelect.innerHTML = '';
    variantSelect.disabled = false;

    if (!product.variants || product.variants.length === 0) {
      variantSelect.innerHTML = '<option value="">No variants available</option>';
      variantSelect.disabled = true;
      return;
    }

    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = sizeOption ? 'Select size' : 'Select option';
    variantSelect.appendChild(defaultOption);

    // Add variants - format display based on options
    // Note: For custom orders, we show ALL variants regardless of availability
    // since these are made-to-order items
    product.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;

      // Build display text from available options
      let displayText = '';
      if (variant.option1) displayText += variant.option1;
      if (variant.option2) displayText += ` / ${variant.option2}`;
      if (variant.option3) displayText += ` / ${variant.option3}`;

      option.textContent = displayText || variant.title;
      option.dataset.price = variant.price;
      option.dataset.option1 = variant.option1 || '';
      option.dataset.option2 = variant.option2 || '';
      option.dataset.option3 = variant.option3 || '';

      variantSelect.appendChild(option);
    });

    // If only one variant (like Default Title), auto-select it
    if (product.variants.length === 1) {
      variantSelect.value = product.variants[0].id;

      // If it's a default title product, hide the variant field
      if (product.variants[0].title === 'Default Title' || product.variants[0].title === 'Default') {
        const formGroup = variantSelect.closest('.form-group-inline');
        formGroup.style.display = 'none';
      }
    }
  }

  updateTotals() {
    const members = this.teamMembersContainer.querySelectorAll('.team-member-card');
    this.totalMembersCount.textContent = members.length;

    let totalItems = 0;
    let totalPrice = 0;

    members.forEach(member => {
      const items = member.querySelectorAll('.member-item');
      items.forEach(item => {
        const qty = parseInt(item.querySelector('.item-quantity').value) || 0;
        totalItems += qty;

        // Calculate price for this item
        const sizeSelect = item.querySelector('.item-size');
        const variantId = sizeSelect.value;

        if (variantId && qty > 0) {
          // Find the variant in products data to get price
          for (const product of this.productsData.products) {
            const variant = product.variants.find(v => v.id == variantId);
            if (variant && variant.price) {
              const price = parseFloat(variant.price);
              totalPrice += price * qty;
              break;
            }
          }
        }
      });
    });

    this.totalItemsCount.textContent = totalItems;

    // Update order estimate
    const estimateEl = document.getElementById('order-estimate');
    if (estimateEl) {
      if (totalPrice > 0) {
        // Format price (assuming it's in cents, convert to Rand)
        const formattedPrice = new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR'
        }).format(totalPrice / 100);
        estimateEl.textContent = formattedPrice;
      } else {
        estimateEl.textContent = 'Select items to see estimate';
      }
    }

    return totalItems;
  }

  async handleSubmit(e) {
    e.preventDefault();

    // Disable submit button
    this.setLoading(true);

    try {
      // Collect form data
      const formData = await this.collectFormData();

      // Get backend endpoint (full URL from Render or app proxy path)
      const section = document.querySelector('[data-app-proxy]');
      const backendEndpoint = section?.getAttribute('data-app-proxy') || 'https://xle-custom-orders-api.onrender.com/a/xle-custom-orders';

      // Submit to backend
      const response = await this.submitOrder(backendEndpoint, formData);

      if (response.success) {
        // Show success modal
        console.log('[Custom Order Form] Order submitted successfully, showing modal');
        this.showSuccessModal();
      } else {
        throw new Error(response.message || 'Failed to submit order');
      }

    } catch (error) {
      console.error('Order submission error:', error);
      this.showMessage(
        error.message || 'There was an error submitting your order. Please try again or contact support.',
        'error'
      );
      this.setLoading(false);
    }
  }

  async collectFormData() {
    const formInputs = new FormData(this.form);

    const data = {
      customer_id: this.form.dataset.customerId,
      customer_email: this.form.dataset.customerEmail,
      order_info: {
        team_name: formInputs.get('team_name'),
        contact_name: formInputs.get('contact_name'),
        contact_email: formInputs.get('contact_email'),
        contact_phone: formInputs.get('contact_phone'),
        delivery_address: {
          address1: formInputs.get('delivery_address'),
          address2: formInputs.get('delivery_address2'),
          city: formInputs.get('delivery_city'),
          province: formInputs.get('delivery_province'),
          zip: formInputs.get('delivery_zip'),
          country: formInputs.get('delivery_country'),
        }
      },
      design_info: {
        description: formInputs.get('design_description'),
        need_design_help: formInputs.get('need_design_help') === 'yes',
      },
      team_members: [],
      additional_info: {
        preferred_deadline: formInputs.get('preferred_deadline'),
        special_requests: formInputs.get('special_requests'),
      },
      submission_date: new Date().toISOString(),
    };

    // Collect team members and their items
    const members = this.teamMembersContainer.querySelectorAll('.team-member-card');
    members.forEach((member, memberIndex) => {
      const memberName = member.querySelector('.member-name-input').value;
      const items = member.querySelectorAll('.member-item');

      // Collect measurements
      const measurements = {};
      const measurementInputs = member.querySelectorAll('.member-measurement');
      measurementInputs.forEach(input => {
        const measurementType = input.dataset.measurement;
        const value = input.value;
        if (value) {
          measurements[measurementType] = parseFloat(value);
        }
      });

      const memberData = {
        name: memberName,
        measurements: measurements,
        items: []
      };

      items.forEach(item => {
        const productSelect = item.querySelector('.item-product');
        const selectedOption = productSelect.options[productSelect.selectedIndex];
        const genderSelect = item.querySelector('.item-gender');
        const sizeSelect = item.querySelector('.item-size');
        const selectedSizeOption = sizeSelect.options[sizeSelect.selectedIndex];
        const fitSelect = item.querySelector('.item-fit');

        const variantId = sizeSelect.value;

        // Validate that we have a variant ID
        if (!variantId) {
          throw new Error(`Please select a size for ${selectedOption.dataset.title || 'the selected product'}`);
        }

        const itemData = {
          product_id: productSelect.value,
          product_title: selectedOption.dataset.title,
          product_type: selectedOption.dataset.type || '',
          variant_id: variantId, // This is the actual variant ID
          gender: genderSelect.value || 'Unisex',
          size: selectedSizeOption ? selectedSizeOption.textContent : '',
          fit_type: fitSelect && fitSelect.closest('.item-fit-group').style.display !== 'none' ? fitSelect.value : '',
          quantity: parseInt(item.querySelector('.item-quantity').value),
        };

        memberData.items.push(itemData);
      });

      data.team_members.push(memberData);
    });

    return data;
  }

  async submitOrder(endpoint, data) {
    console.log('Submitting order to:', endpoint);
    console.log('Order data:', data);

    // Create FormData for file uploads
    const formData = new FormData();

    // Append the order data as JSON
    formData.append('data', JSON.stringify(data));

    // Append design files
    this.uploadedFiles.design.forEach((file) => {
      formData.append('design_files', file);
    });

    // Append logo files
    this.uploadedFiles.logo.forEach((file) => {
      formData.append('logo_files', file);
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If response is not JSON, try to get text
        try {
          const errorText = await response.text();
          console.error('Server error response:', errorText);
          errorMessage = errorText.substring(0, 200); // First 200 chars
        } catch (textError) {
          console.error('Could not parse error response');
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  setLoading(loading) {
    this.submitBtn.disabled = loading;
    const buttonText = this.submitBtn.querySelector('.button-text');
    const buttonLoader = this.submitBtn.querySelector('.button-loader');

    if (loading) {
      buttonText.style.display = 'none';
      buttonLoader.style.display = 'inline-block';
    } else {
      buttonText.style.display = 'inline';
      buttonLoader.style.display = 'none';
    }
  }

  showMessage(message, type = 'info') {
    this.messagesDiv.textContent = message;
    this.messagesDiv.className = `form-messages ${type}`;
    this.messagesDiv.style.display = 'block';

    // Scroll to message
    this.messagesDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Auto-hide success messages
    if (type === 'success') {
      setTimeout(() => {
        this.messagesDiv.style.display = 'none';
      }, 5000);
    }
  }

  showSuccessModal() {
    const modal = document.getElementById('order-success-modal');
    if (!modal) {
      console.error('Success modal not found');
      return;
    }

    // Show modal
    modal.style.display = 'flex';

    // Add event listener to Okay button
    const okayBtn = document.getElementById('modal-okay-btn');
    if (okayBtn) {
      okayBtn.addEventListener('click', () => {
        // Redirect to home page
        window.location.href = '/';
      });
    }

    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
  }

  // Enable editing of contact fields
  enableContactEditing() {
    const contactFields = document.querySelectorAll('.contact-field');
    const editBtn = document.getElementById('edit-contact-btn');
    const saveBtn = document.getElementById('save-contact-btn');
    const cancelBtn = document.getElementById('cancel-contact-btn');
    const displayDiv = document.getElementById('contact-info-display');
    const fieldsDiv = document.getElementById('contact-info-fields');

    // Store original values
    contactFields.forEach(field => {
      this.originalContactData[field.id] = field.value;
      field.removeAttribute('readonly');
      field.classList.add('editing');
    });

    // Toggle display and fields
    if (displayDiv) displayDiv.style.display = 'none';
    if (fieldsDiv) fieldsDiv.style.display = 'block';

    // Toggle buttons
    editBtn.style.display = 'none';
    saveBtn.style.display = 'inline-block';
    cancelBtn.style.display = 'inline-block';
  }

  // Save contact info to Shopify customer account
  async saveContactInfo() {
    const saveBtn = document.getElementById('save-contact-btn');
    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Saving...';
    saveBtn.disabled = true;

    const customerData = {
      name: document.getElementById('contact-name').value,
      email: document.getElementById('contact-email').value,
      phone: document.getElementById('contact-phone').value,
      address: {
        address1: document.getElementById('delivery-address').value,
        address2: document.getElementById('delivery-address2').value,
        city: document.getElementById('delivery-city').value,
        province: document.getElementById('delivery-province').value,
        zip: document.getElementById('delivery-zip').value,
        country: document.getElementById('delivery-country').value
      }
    };

    try {
      const response = await fetch('https://xle-custom-orders-api.onrender.com/a/xle-custom-orders/update-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customerData)
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If can't parse JSON, try to get text
          try {
            const errorText = await response.text();
            errorMessage = errorText.substring(0, 200);
          } catch (textError) {
            // Keep default error message
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        this.showContactMessage('Contact information updated successfully', 'success');
        this.updateContactDisplay();
        this.disableContactEditing();
      } else {
        throw new Error(result.message || 'Failed to update contact information');
      }
    } catch (error) {
      console.error('Error saving contact info:', error);
      this.showContactMessage('Failed to save contact information: ' + error.message, 'error');
    } finally {
      saveBtn.textContent = originalText;
      saveBtn.disabled = false;
    }
  }

  // Cancel editing and restore original values
  cancelContactEditing() {
    const contactFields = document.querySelectorAll('.contact-field');

    // Restore original values
    contactFields.forEach(field => {
      if (this.originalContactData[field.id] !== undefined) {
        field.value = this.originalContactData[field.id];
      }
    });

    this.disableContactEditing();
  }

  // Show message in contact section
  showContactMessage(message, type = 'info') {
    const contactMessagesDiv = document.getElementById('contact-messages');
    if (!contactMessagesDiv) return;

    contactMessagesDiv.textContent = message;
    contactMessagesDiv.className = `form-messages ${type}`;
    contactMessagesDiv.style.display = 'block';

    // Auto-hide success messages
    if (type === 'success') {
      setTimeout(() => {
        contactMessagesDiv.style.display = 'none';
      }, 5000);
    }
  }

  // Update contact info display text
  updateContactDisplay() {
    const nameSpan = document.getElementById('display-name');
    const emailSpan = document.getElementById('display-email');
    const phoneSpan = document.getElementById('display-phone');
    const addressSpan = document.getElementById('display-address');

    if (nameSpan) nameSpan.textContent = document.getElementById('contact-name').value;
    if (emailSpan) emailSpan.textContent = document.getElementById('contact-email').value;
    if (phoneSpan) phoneSpan.textContent = document.getElementById('contact-phone').value;

    if (addressSpan) {
      const addr1 = document.getElementById('delivery-address').value;
      const addr2 = document.getElementById('delivery-address2').value;
      const city = document.getElementById('delivery-city').value;
      const province = document.getElementById('delivery-province').value;
      const zip = document.getElementById('delivery-zip').value;
      const country = document.getElementById('delivery-country').value;

      let addressText = addr1;
      if (addr2) addressText += ', ' + addr2;
      addressText += ', ' + city + ' ' + province + ' ' + zip + ', ' + country;
      addressSpan.textContent = addressText;
    }
  }

  // Disable editing mode
  disableContactEditing() {
    const contactFields = document.querySelectorAll('.contact-field');
    const editBtn = document.getElementById('edit-contact-btn');
    const saveBtn = document.getElementById('save-contact-btn');
    const cancelBtn = document.getElementById('cancel-contact-btn');
    const displayDiv = document.getElementById('contact-info-display');
    const fieldsDiv = document.getElementById('contact-info-fields');

    contactFields.forEach(field => {
      field.setAttribute('readonly', 'readonly');
      field.classList.remove('editing');
    });

    // Toggle display and fields
    if (displayDiv) displayDiv.style.display = 'block';
    if (fieldsDiv) fieldsDiv.style.display = 'none';

    // Toggle buttons
    editBtn.style.display = 'inline-block';
    saveBtn.style.display = 'none';
    cancelBtn.style.display = 'none';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomBulkOrderFormV2();
  });
} else {
  new CustomBulkOrderFormV2();
}
